const LiquifyHelper = {
    /**
     * Format a monetary value with the currency-format set in Shopify backend.
     *
     * @int value
     * @bool minor Optional, defaults to true.
     * @string shopMoneyFormat Shop-Setting, via Liquid: shop.money_with_currency_format
     */
    moneyFormat: (value, isMinor = true, shopMoneyFormat = '') => {
        const stripTrailingZeros = (formatted) => String(formatted).replace(/[.,]00(?!\d)/, '');
        let floatValue = parseFloat(value);

        if (isNaN(floatValue)) {
            return '';
        }

        floatValue = isMinor
            ? floatValue / 100
            : floatValue;

        let formattedValue = '';
        const match = shopMoneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
        const formatType = match ? match[1] : null;

        switch (formatType) {
            case 'amount':
                formattedValue = floatValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                break;
            case 'amount_no_decimals':
                formattedValue = Math.round(floatValue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                break;
            case 'amount_with_comma_separator':
                formattedValue = floatValue.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                break;
            default:
                console.log('Error in moneyFormat. Unknown format type or none provided: ' + formatType);

                const formatter = new Intl.NumberFormat(Shopify.locale + '-' + Shopify.country, {
                    style: 'currency',
                    currency: Shopify.currency.active,
                });

                value = isMinor ? value / 100 : value;

                return stripTrailingZeros(formatter.format(value));
        }

        return stripTrailingZeros(shopMoneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, formattedValue));
    },

    htmlspecialchars_decode(string) {
        let tempElement = document.createElement('textarea');
        tempElement.innerHTML = string;
        return tempElement.value;
    },

    triggerClick: function (el) {
        if(!(el instanceof HTMLElement)) {
            console.info(el + ' must be an instance of HTMLElement')
            return
        }
        const ev = new KeyboardEvent("keydown",{
            code: 'Enter',
            key: 'Enter',
            charCode: 13,
            keyCode: 13,
            view: window,
            bubbles: true
        });

        el.dispatchEvent(ev);
    },

    /**
     * Trigger dropdown click to open minicart
     */
    handleTriggerClick: function () {
        let elements = document.querySelectorAll('[li-element="mini-cart-toggle"]');
        elements.forEach(element => {
            let closestDropdown = element.closest('.w-dropdown-toggle');
            if (closestDropdown) {
                this.triggerClick(closestDropdown);
            } else {
                console.error('(helper.js) No dropdown found for minicart trigger');
            }
        });
    },

    triggerClickById: (id) => {
        const el = document.getElementById(id)
        LiquifyHelper.triggerClick(el)
    },

    triggerClickByClass: (className) => {
        const elList = document.getElementsByClassName(className)
        for (const el of elList) {
            LiquifyHelper.triggerClick(el)
        }
    },

    serializeFormToObject: (form) => {
        const obj = {};
        try {
            const formData = new FormData(form);
            const properties = {};
            for (const [key, value] of formData) {
                if(key.indexOf('properties') !== -1) {
                    const name = key.replace('properties[', '').replace(']', '');
                    properties[name] = value;
                    obj["properties"] = properties;
                } else {
                    obj[key] = value;
                }
            }
        } catch(e) {
            console.info("form not found", form)
        }
        return obj ;
    },
};

/**
 * Simple legal page Table of Contents builder.
 * - Scans #legal-contents for H2–H6
 * - Ensures unique IDs
 * - Injects links into #toc-target using existing Webflow classes
 * - Highlights the current section on scroll by toggling `current` on the link
 */
LiquifyHelper.slugify = function (text) {
    return text
        .toString()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-');
};

LiquifyHelper.buildTableOfContents = function () {
    try {
        const contents = document.getElementById('legal-contents');
        const tocTarget = document.getElementById('toc-target');
        if (!contents || !tocTarget) return;

        const headings = contents.querySelectorAll('h2, h3, h4, h5, h6');
        if (headings.length === 0) {
            const sidebar = document.querySelector('.content27_sidebar');
            if (sidebar) sidebar.style.display = 'none';
            return;
        }

        // Utilities for building a hierarchical list
        const used = new Set();
        const getOrMakeId = (heading) => {
            let id = heading.getAttribute('id') || LiquifyHelper.slugify(heading.textContent);
            const base = id;
            let i = 2;
            while (used.has(id) || document.getElementById(id)) id = `${base}-${i++}`;
            used.add(id);
            heading.id = id;
            return id;
        };

        const createLink = (id, level, text) => {
            const a = document.createElement('a');
            a.className = `content27_link w-inline-block${level > 2 ? ` is-h${level}` : ''}`;
            a.href = `#${id}`;
            const label = document.createElement('div');
            label.className = 'text-size-medium';
            label.setAttribute('fs-toc-element', 'link');
            label.textContent = text;
            a.appendChild(label);
            return a;
        };

        // Keep a stack of the latest container for each level
        const levelToContainer = new Map();
        const getParentContainerForLevel = (level) => {
            for (let l = level - 1; l >= 2; l--) {
                if (levelToContainer.has(l)) return levelToContainer.get(l);
            }
            return tocTarget;
        };

        headings.forEach(h => {
            const id = getOrMakeId(h);
            const level = parseInt(h.tagName.substring(1), 10);

            const wrapper = document.createElement('div');
            wrapper.className = `content27_link-wrapper is-h${level}`;

            const a = createLink(id, level, h.textContent);
            wrapper.appendChild(a);

            // Add expand/collapse trigger placeholder for H2 (and optionally H3)
            if (level === 2) {
                const trigger = document.createElement('div');
                trigger.className = 'content27_h-trigger';
                trigger.setAttribute('fs-toc-element', 'ix-trigger');
                wrapper.appendChild(trigger);
                wrapper.classList.add('has-children'); // collapsed by default via CSS
            }

            // Children container for nested headings under this wrapper
            const children = document.createElement('div');
            children.className = 'content27_children';
            wrapper.appendChild(children);

            // Append to appropriate parent container
            const parent = (level === 2) ? tocTarget : getParentContainerForLevel(level);
            parent.appendChild(wrapper);

            // Update the registered container for this level and clear deeper levels
            levelToContainer.set(level, children);
            for (let l = level + 1; l <= 6; l++) levelToContainer.delete(l);

            // Mark first link as current for initial render
            if (!tocTarget.querySelector('a.content27_link.current')) {
                a.classList.add('current');
            }
        });

        // Scroll spy to toggle "current" with suppression during programmatic smooth scroll
        const linkFor = id => tocTarget.querySelector(`a[href="#${id}"]`);
        const allLinks = () => tocTarget.querySelectorAll('a.content27_link');
        const clearAllStates = () => {
            allLinks().forEach(a => {
                a.classList.remove('current');
                a.classList.remove('w--current');
            });
        };
        const findH2Wrapper = (el) => {
            if (!el) return null;
            let node = el.closest('.content27_link-wrapper');
            while (node && node !== tocTarget) {
                if (node.classList && node.classList.contains('is-h2')) return node;
                node = node.parentElement?.closest?.('.content27_link-wrapper');
            }
            return null;
        };
        const openH2Exclusive = (wrapperToOpen) => {
            const allH2 = tocTarget.querySelectorAll('.content27_link-wrapper.is-h2');
            allH2.forEach(w => {
                if (w === wrapperToOpen) {
                    w.classList.add('is-open');
                } else {
                    w.classList.remove('is-open');
                }
            });
        };
        const getScrollContainer = () => tocTarget.closest('.content27_link-content') || tocTarget;
        const revealAncestors = (link) => {
            if (!link) return;
            // Open all parent wrappers so nested links are visible
            let node = link.parentElement;
            while (node && node !== tocTarget) {
                if (node.classList && node.classList.contains('content27_link-wrapper')) {
                    node.classList.add('is-open');
                }
                node = node.parentElement;
            }
        };
        const scrollActiveIntoView = (link) => {
            if (!link) return;
            const container = getScrollContainer();
            const containerRect = container.getBoundingClientRect();
            const linkRect = link.getBoundingClientRect();
            const above = linkRect.top < containerRect.top;
            const below = linkRect.bottom > containerRect.bottom;
            if (above || below) {
                // Center the active link within the container
                const offsetTopWithin = (el, ancestor) => {
                    let y = 0;
                    let n = el;
                    while (n && n !== ancestor) {
                        y += n.offsetTop;
                        n = n.offsetParent;
                    }
                    return y;
                };
                const linkTop = offsetTopWithin(link, container);
                const targetScrollTop = Math.max(0, linkTop - container.clientHeight / 2 + link.clientHeight / 2);
                container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
            }
        };
        const setCurrentLink = (link) => {
            clearAllStates();
            if (link) {
                link.classList.add('current');
                revealAncestors(link);
                // Ensure only one H2 group is open at a time
                openH2Exclusive(findH2Wrapper(link));
                scrollActiveIntoView(link);
            }
        };
        let suppressObserverUntil = 0;
        const observer = new IntersectionObserver(entries => {
            if (performance.now() < suppressObserverUntil) return;
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                setCurrentLink(linkFor(entry.target.id));
            });
        }, { rootMargin: '-40% 0px -40% 0px', threshold: 0.1 });
        headings.forEach(h => observer.observe(h));

        // Ensure Webflow's auto-assigned w--current doesn't conflict inside TOC
        clearAllStates();

        // Smooth scroll with slight offset for sticky header
        tocTarget.addEventListener('click', e => {
            const a = e.target.closest('a[href^="#"]');
            if (!a) return;
            const id = a.getAttribute('href').slice(1);
            const el = document.getElementById(id);
            if (!el) return;
            e.preventDefault();
            if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
            e.stopPropagation();

            // Immediately reflect the clicked item as active
            setCurrentLink(a);

            // Compute duration-based suppression to avoid flicker during smooth scroll
            const y = el.getBoundingClientRect().top + window.scrollY - 80;
            const distance = Math.abs(window.scrollY - y);
            const duration = Math.min(1500, Math.max(500, Math.round(distance / 2))); // ms
            suppressObserverUntil = performance.now() + duration + 100;

            window.scrollTo({ top: y, behavior: 'smooth' });

            // Keep URL hash in sync without causing a native jump
            if (history && typeof history.replaceState === 'function') {
                history.replaceState(null, '', `#${id}`);
                // Remove any w--current that Webflow may toggle based on hash, but keep our .current
                tocTarget.querySelectorAll('a.content27_link.w--current').forEach(a => a.classList.remove('w--current'));
            }
        }, { passive: false });

        // Expand/collapse behavior for H2 wrappers (accordion: single open at a time)
        tocTarget.addEventListener('click', e => {
            const trigger = e.target.closest('.content27_h-trigger');
            if (!trigger) return;
            const wrapper = trigger.closest('.content27_link-wrapper');
            if (!wrapper) return;
            openH2Exclusive(wrapper);
            const h2Link = wrapper.querySelector('a.content27_link');
            if (h2Link) setCurrentLink(h2Link);
        });

        // When landing on a deep hash, ensure the correct section is open and visible
        if (location.hash) {
            const deep = linkFor(location.hash.replace('#', ''));
            if (deep) setCurrentLink(deep);
        } else {
            // Open the first H2 by default for a compact/clear initial view
            const firstH2Wrapper = tocTarget.querySelector('.content27_link-wrapper.is-h2');
            if (firstH2Wrapper) openH2Exclusive(firstH2Wrapper);
        }
    } catch (e) {
        console.warn('LiquifyHelper.buildTableOfContents failed', e);
    }
};

document.addEventListener('DOMContentLoaded', () => {
    try {
        LiquifyHelper.buildTableOfContents();
        // Guard against external hash changes (e.g., back/forward)
        window.addEventListener('hashchange', () => {
            try {
                const tocTarget = document.getElementById('toc-target');
                if (!tocTarget) return;
                tocTarget.querySelectorAll('a.content27_link').forEach(a => a.classList.remove('w--current'));
            } catch (e) {}
        });
    } catch (e) {}
});
